import { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import { Asset } from 'contentful';
import { Menu } from '@material-ui/icons';

import Link from '../UI/Link';
import Image from '../UI/Image';
import NavLink from '../UI/Nav/NavLink';
import SubNavLink from '../UI/Nav/SubNavLink';
import MobileNav from '../UI/MobileNav/MobileNav';
import CTA from '../UI/Cta';
import { isNavLinkGuard } from 'types/nav';
import { INavHeaderFields } from 'types/contentful';
// import dynamic from 'next/dynamic';
import { ScrollContext } from 'context/ScrollContext';
import HeaderAlertBanner from 'components/UI/HeaderAlertBanner';
import PromotionalBanner from 'components/UI/PromotionalBanner';
import PhoneIconButton from 'components/UI/PhoneIconButton';
// import HeaderContactNumbers from 'components/UI/HeaderContactNumber';
import { LINK_CONTENT_TYPE } from 'lib/consts';
import IFrameCTA from 'components/UI/IFrameCta';
import { useFactua } from 'context/FactuaContext';

// const Search = dynamic<{ onClick?: () => void; iconOnlyDesktop?: boolean }>(
//   () => import('components/UI/Search'),
//   { ssr: false }
// );

interface TopNavProps extends INavHeaderFields {
  logo: Asset;
  currentSlug: string;
  siteName: string;
  hideTopNavCtas?: boolean;
}

export default function TopNav({
  links,
  logo,
  currentSlug,
  ctas,
  hideTopNavCtas,
  phoneNumbers,
  phoneNumberIcon = 'Default',
  alertBanner,
  headerType,
  belowHeaderBanner,
}: TopNavProps): ReactElement {
  const [mobileNav, setMobileNav] = useState(false);
  const [onBannerRemove, setOnBannerRemove] = useState<boolean>(false);
  const { navShowing, setHeaderHeight, headerHeight } = useContext(ScrollContext);
  const ref = useRef<HTMLElement>(null);
  let headerTypeClass = 'hidden';
  const { openFactua } = useFactua();

  useEffect(() => {
    if (ref.current) {
      setHeaderHeight(ref.current.clientHeight);
    }
  }, [setHeaderHeight, ref?.current?.clientHeight, onBannerRemove]);

  // If headerType is ECPRebranding only than add the styleclass
  // ecp-rebrand-header to the dummy layer
  if (headerType === 'Rebranding') {
    headerTypeClass = 'ecp-rebrand-header hidden';
  }

  const linkList = links
    ? links.map((link, index) => {
        if (isNavLinkGuard(link)) {
          return (
            <NavLink
              key={index}
              currentSlug={currentSlug}
              classNames={`flex-none py-4 text-secondary font-heading lg:text-base max:text-lg ${
                currentSlug === link.fields.slug ? 'border-b-3 border-black font-bold' : ''
              }`}
              {...link.fields}
            />
          );
        } else {
          return <SubNavLink key={index} link={link} currentSlug={currentSlug} />;
        }
      })
    : [];

  const ctaList =
    ctas?.map((cta, index) => {
      const linkContentType = cta.sys.contentType.sys.id;
      if (linkContentType === LINK_CONTENT_TYPE.fragmentIFrameCta) {
        return (
          <IFrameCTA
            key={index}
            {...cta.fields}
            backgroundColor="black"
            classNames="text-base"
            replaceBaseClasses={['text-lg']}
            onClick={() => openFactua(cta.fields.slug)}
          />
        );
      }

      return (
        <CTA
          key={index}
          {...cta.fields}
          backgroundColor="black"
          classNames="text-base"
          replaceBaseClasses={['text-lg']}
        />
      );
    }) || [];

  const toggleDrawer = (open: boolean) => () => {
    setMobileNav(open);
  };

  return (
    <header
      ref={ref}
      className="w-full fixed bg-white z-20 transition-all ease-out duration-300 flex-none"
      style={{
        boxShadow: '0px 4px 14px rgba(100, 100, 100, 0.15)',
        top: navShowing ? 0 : headerHeight * -1,
        zIndex: 50,
      }}
    >
      {alertBanner && (
        <HeaderAlertBanner
          text={alertBanner.fields.bannerText}
          colorScheme={alertBanner.fields.colorScheme}
          openInNewTab={alertBanner.fields.openInNewTab}
          setOnBannerRemove={setOnBannerRemove}
        />
      )}

      {belowHeaderBanner && <PromotionalBanner {...belowHeaderBanner.fields} />}

      <MobileNav
        isOpen={mobileNav}
        toggle={toggleDrawer}
        close={setMobileNav}
        currentSlug={currentSlug}
        links={links ? links : []}
        hideTopNavCtas={hideTopNavCtas}
        ctas={ctas}
        phoneNumberIcon={phoneNumberIcon}
        numbers={phoneNumbers}
      />

      <div className="max:container max-w-screen-max">
        <div className="flex flex-wrap items-center justify-between px-2 lg:px-2 py-4">
          <div className="flex items-center xl:mr-2">
            <div className={headerTypeClass}></div>
            <div className="w-full cursor-pointer">
              <Link slug="home">
                <a aria-label="home" className="block w-full">
                  <Image
                    image={logo}
                    query="h=60"
                    classNames="sm:max-w-300 w-headerLogoMobile sm:w-headerLogo max-h-40 sm:max-h-60 w-full"
                  />
                </a>
              </Link>
            </div>
          </div>

          <nav
            role="navigation"
            className="hidden md:flex justify-start pl-3 px-0 w-auto xl:flex-grow xl:justify-center xl:px-4 header-nav-center-links"
          >
            <div className="flex items-center justify-start flex-wrap gap-center-nav xl:justify-center">
              {linkList}
            </div>
          </nav>

          <div className="hidden md:flex items-center header-nav-desktop-ctas">
            {/* {searchEnabled && <Search iconOnlyDesktop />} */}
            {!hideTopNavCtas && (
              <div className="flex items-center gap-3 ml-3">
                {/* {phoneNumbers &&
                  (phoneNumberIcon === '2025' ? (
                    <PhoneIconButton numbers={phoneNumbers} width={40} height={40} />
                  ) : (
                    <HeaderContactNumbers numbers={phoneNumbers} />
                  ))} */}
                {ctaList}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 ml-4 md:hidden header-nav-mobile-ctas">
            {phoneNumberIcon === '2025' && phoneNumbers && (
              <PhoneIconButton numbers={phoneNumbers} width={40} height={40} />
            )}
            <button
              type="button"
              role="menu"
              aria-label="open navigation menu"
              className="self-center"
              onClick={toggleDrawer(true)}
            >
              <Menu fontSize="large" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
